import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CameraControlRequest,
    SaveCameraRequest,
} from '@/lib/api/cameras-api'
import { camerasApi } from '@/lib/api/cameras-api'
import { useAuth } from '@/hooks/use-auth'
import { ApiError } from '@/lib/api/client'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { m } from '@/lib/paraglide/messages'

export const cameraKeys = {
    all: ['cameras'] as const,
    list: ['cameras', 'list'] as const,
    byArea: (areaId: string) => ['cameras', 'area', areaId] as const,
    byDevice: (deviceId: string) => ['cameras', 'device', deviceId] as const,
}

/** Mọi camera kèm nơi đặt, cho màn quản lý / tổng hợp camera. */
export function useCameras() {
    return useQuery({
        queryKey: cameraKeys.list,
        queryFn: async () => {
            const res = await camerasApi.list()
            return res.data ?? []
        },
    })
}

/**
 * Camera của một vùng trồng. `areaId` rỗng (vùng chưa lưu) thì không gọi API —
 * vùng chưa có id thì chưa gắn được camera. Không có cameras.read cũng không
 * gọi: màn hộ trồng mở theo growers.read, gọi thì chỉ nhận 403.
 */
export function useGrowerAreaCameras(areaId: string | undefined) {
    const { hasPermission } = useAuth()
    return useQuery({
        queryKey: cameraKeys.byArea(areaId ?? ''),
        enabled: Boolean(areaId) && hasPermission('cameras.read'),
        queryFn: async () => {
            const res = await camerasApi.list(areaId as string)
            return res.data ?? []
        },
    })
}

/** Camera của một trạm bơm. Không có cameras.read thì không gọi API. */
export function useIrrigationDeviceCameras(deviceId: string) {
    const { hasPermission } = useAuth()
    return useQuery({
        queryKey: cameraKeys.byDevice(deviceId),
        enabled: hasPermission('cameras.read'),
        queryFn: async () => {
            const res = await camerasApi.listByIrrigationDevice(deviceId)
            return res.data ?? []
        },
    })
}

function messageOf(error: unknown, fallback: string) {
    return error instanceof ApiError && error.message ? error.message : fallback
}

/**
 * Mọi thay đổi camera làm mới CẢ nhánh `cameras`: một camera hiện ở danh sách
 * chung lẫn ở vùng nó đang gắn (và chuyển vùng là đổi hai vùng một lúc).
 */
function useInvalidateCameras() {
    const qc = useQueryClient()
    return () => void qc.invalidateQueries({ queryKey: cameraKeys.all })
}

/**
 * Thêm hoặc sửa camera. Thêm mới có thể gắn luôn vào một vùng
 * (`growerAreaId`, dùng ở tab camera của vùng); sửa chỉ đổi tên và token.
 */
export function useSaveCamera() {
    const invalidate = useInvalidateCameras()
    return useMutation({
        mutationFn: async (vars: {
            cameraId?: string
            growerAreaId?: string
            irrigationDeviceId?: string
            data: SaveCameraRequest
        }) => {
            const res = vars.cameraId
                ? await camerasApi.update(vars.cameraId, vars.data)
                : await camerasApi.create({
                      ...vars.data,
                      grower_area_id: vars.growerAreaId ?? null,
                      irrigation_device_id: vars.irrigationDeviceId ?? null,
                  })
            return res.data
        },
        onSuccess: (_data, vars) => {
            invalidate()
            toast.success(
                vars.cameraId
                    ? m.grower_area_camera_toast_updated()
                    : m.grower_area_camera_toast_created(),
            )
        },
        onError: (error) =>
            toast.error(messageOf(error, m.grower_area_camera_save_error())),
    })
}

export function useDeleteCamera() {
    const invalidate = useInvalidateCameras()
    return useMutation({
        mutationFn: async (cameraId: string) => {
            await camerasApi.remove(cameraId)
            return cameraId
        },
        onSuccess: () => {
            invalidate()
            toast.success(m.grower_area_camera_deleted())
        },
        onError: (error) =>
            toast.error(messageOf(error, m.grower_area_camera_delete_error())),
    })
}

/** Gắn camera vào vùng / chuyển vùng; `growerAreaId` null = gỡ khỏi vùng. */
export function useSetCameraLocation() {
    const invalidate = useInvalidateCameras()
    return useMutation({
        mutationFn: async (vars: {
            cameraId: string
            growerAreaId: string | null
        }) => {
            const res = await camerasApi.setLocation(
                vars.cameraId,
                vars.growerAreaId,
            )
            return res.data
        },
        onSuccess: (_data, vars) => {
            invalidate()
            toast.success(
                vars.growerAreaId
                    ? m.camera_location_placed()
                    : m.camera_location_removed(),
            )
        },
        onError: (error) =>
            toast.error(messageOf(error, m.camera_location_error())),
    })
}

/** Gắn (`attach: true`) hoặc gỡ camera khỏi một trạm bơm. */
export function useSetIrrigationDeviceCamera() {
    const invalidate = useInvalidateCameras()
    return useMutation({
        mutationFn: async (vars: {
            cameraId: string
            deviceId: string
            attach: boolean
        }) => {
            const res = vars.attach
                ? await camerasApi.attachIrrigationDevice(
                      vars.cameraId,
                      vars.deviceId,
                  )
                : await camerasApi.detachIrrigationDevice(
                      vars.cameraId,
                      vars.deviceId,
                  )
            return res.data
        },
        onSuccess: (_data, vars) => {
            invalidate()
            toast.success(
                vars.attach
                    ? m.camera_station_attached()
                    : m.camera_station_detached(),
            )
        },
        // Lỗi kiểm trạm dùng mã của màn trạm bơm (IRRIGATION_*), backend chỉ
        // trả mã nên phải dịch ở đây.
        onError: (error) =>
            toast.error(
                (error instanceof ApiError &&
                    resolveApiErrorMessage(error.code)) ||
                    messageOf(error, m.camera_location_error()),
            ),
    })
}

export function useTestCamera() {
    return useMutation({
        mutationFn: async (cameraId: string) => {
            const res = await camerasApi.test(cameraId)
            return res.data ?? null
        },
        // Không toast thành công: kết quả hiện ngay trong hộp thoại, toast nữa
        // là nhân đôi thông tin.
        onError: (error) =>
            toast.error(messageOf(error, m.grower_area_camera_check_error())),
    })
}

/**
 * Gửi một lệnh PTZ. KHÔNG invalidate query nào: quay camera không đổi dữ liệu,
 * và mỗi lần bấm giữ bắn 2 lệnh (bat_dau + dung) nên refetch là vô ích.
 *
 * `cameraId` đi THEO TỪNG LỆNH chứ không gắn vào hook: lệnh `dung` thường được
 * gửi đúng lúc hộp thoại đóng hoặc vừa chuyển sang camera khác. Gắn id ở hook
 * thì mutation (chạy bất đồng bộ) đọc phải id của lần render sau — rỗng hoặc
 * của camera mới — và camera cũ cứ thế quay.
 *
 * Lỗi của lệnh `dung` được nuốt có chủ đích ở call-site (xem use-camera-ptz):
 * toast lỗi lúc nhả nút chỉ làm người dùng bối rối khi camera đã dừng.
 */
export function useControlCamera() {
    return useMutation({
        mutationFn: async (vars: {
            cameraId: string
            command: CameraControlRequest
        }) => {
            await camerasApi.control(vars.cameraId, vars.command)
        },
    })
}
